

















































import {
  computed,
  defineComponent,
  Ref,
  ref,
  toRefs
} from '~/utils/nuxt3-migration'
import { DYNAMIC_FORM_FIELD_PROPS } from '~/constants/dynamic-form'
import { useDynamicForm } from '~/compositions/dynamic-form'
import CTable from '~/components/shared/configurable/table/CTable.vue'
import CTableHead from '~/components/shared/configurable/table/CTableHead.vue'
import CTableRow from '~/components/shared/configurable/table/CTableRow.vue'
import CTableHeadCell from '~/components/shared/configurable/table/CTableHeadCell.vue'
import CTableBody from '~/components/shared/configurable/table/CTableBody.vue'
import { useI18n } from '~/compositions/i18n'
import { Option } from '~/models/shared/types'
import { range } from '~/utils/array'

export default defineComponent({
  components: { CTableBody, CTableHeadCell, CTableRow, CTableHead, CTable },
  props: DYNAMIC_FORM_FIELD_PROPS,
  setup(props) {
    const { name } = toRefs(props)

    const schedule = ref<Record<string, Record<string, any>>>({
      monday: { from: null, to: null },
      tuesday: { from: null, to: null },
      friday: { from: null, to: null },
      wednesday: { from: null, to: null },
      thursday: { from: null, to: null },
      sunday: { from: null, to: null },
      saturday: { from: null, to: null }
    })

    const fromTimeOptions: Option[] = [
      { text: '06:00', value: 6 },
      { text: '07:00', value: 7 },
      { text: '08:00', value: 8 },
      { text: '09:00', value: 9 },
      { text: '10:00', value: 10 },
      { text: '11:00', value: 11 },
      { text: '12:00', value: 12 },
      { text: '13:00', value: 13 },
      { text: '14:00', value: 14 },
      { text: '15:00', value: 15 },
      { text: '16:00', value: 16 },
      { text: '17:00', value: 17 },
      { text: '18:00', value: 18 },
      { text: '19:00', value: 19 },
      { text: '20:00', value: 20 },
      { text: '21:00', value: 21 },
      { text: '22:00', value: 22 },
      { text: '23:00', value: 23 }
    ]

    const nextDayOptions: Option[] = [
      { text: '00:00', value: 0, nextDay: true },
      { text: '01:00', value: 1, nextDay: true },
      { text: '02:00', value: 2, nextDay: true },
      { text: '03:00', value: 3, nextDay: true },
      { text: '04:00', value: 4, nextDay: true },
      { text: '05:00', value: 5, nextDay: true }
    ]

    const toTimeOptions: Option[] = [...fromTimeOptions, ...nextDayOptions]

    function onValue(v: Ref<any>) {
      if (!v.value) {
        return
      }
      Object.entries(v.value).forEach(([day, hours]: any) => {
        if (hours && hours.length) {
          let fromToSet
          let toToSet
          if (hours.length > 1) {
            fromToSet = parseInt(hours[0])
            toToSet = parseInt(hours[hours.length - 1]) + 1
            if (toToSet === 24) {
              toToSet = 0
            }
          } else if (hours.length === 1) {
            const theOneValue = parseInt(hours[0])
            if (
              (theOneValue !== 23 &&
                nextDayOptions.some(o => o.value === theOneValue + 1)) ||
              (schedule.value[day].to && !schedule.value[day].from)
            ) {
              toToSet = theOneValue + 1
              fromToSet = null
            } else {
              fromToSet = theOneValue
            }
          }

          if (toToSet && fromToSet && toToSet === fromToSet) {
            toToSet = fromToSet + 1
          }

          schedule.value[day].from = fromToSet
          schedule.value[day].to = toToSet
        }
      })
    }

    const { error, changeValue } = useDynamicForm(props, { onValue })
    const { t } = useI18n()

    const days = ref([
      { name: 'monday', label: t('monday') },
      { name: 'tuesday', label: t('tuesday') },
      { name: 'wednesday', label: t('wednesday') },
      { name: 'thursday', label: t('thursday') },
      { name: 'friday', label: t('friday') },
      { name: 'saturday', label: t('saturday') },
      { name: 'sunday', label: t('sunday') }
    ])

    function handleFromTimeInput(dayName: string, value: number) {
      schedule.value[dayName].from = value
      updateValue()
    }

    function handleToTimeInput(dayName: string, value: number) {
      schedule.value[dayName].to = value
      updateValue()
    }

    function updateValue() {
      changeValue(name.value, transformedSchedule.value)
    }

    function toTimeOptionIsDisabled(dayName: string, option: any) {
      let selectedFrom = schedule.value[dayName].from
      if (selectedFrom) {
        // disable options from the next hour in order to avoid buggy behavior
        // there should always be a two hour distance
        selectedFrom++
      }

      if (selectedFrom === 24) {
        if (option.nextDay) {
          selectedFrom = 0
        }
        return option.value <= selectedFrom
      }
      return !!(selectedFrom && option.value <= selectedFrom && !option.nextDay)
    }

    function fromTimeOptionIsDisabled(dayName: string, option: any) {
      let selectedTo = schedule.value[dayName].to
      if (selectedTo) {
        // there should always be a two hour distance
        selectedTo--
      } else if (selectedTo === 0) {
        selectedTo = 23
      }
      const selectedToIsNextDay = nextDayOptions.some(
        o => o.value === selectedTo
      )
      return !!(
        selectedTo &&
        option.value >= selectedTo &&
        !selectedToIsNextDay
      )
    }

    const transformedSchedule = computed(() => {
      const transformed: Record<string, any> = {}

      for (const [day, times] of Object.entries(schedule.value)) {
        const { from, to } = times
        const toIsNextDay = nextDayOptions.some(o => o.value === to)
        if (!(from || to) && !(from === 0 || to === 0)) {
          transformed[day] = []
        } else if ((from && !to && to !== 0) || (to && !from && from !== 0)) {
          transformed[day] = [from ?? parseInt(to) - 1]
        } else if (from < to || toIsNextDay) {
          if (toIsNextDay) {
            transformed[day] = range(from, 23)
            // since this is next day it definitely has midnight
            if (to !== 0) {
              transformed[day].push(0)
            }
            if (parseInt(to) - 1 > 0) {
              // if selected to is larger than midnight add it
              transformed[day] = [
                ...transformed[day],
                ...range(1, parseInt(to) - 1)
              ]
            }
          } else {
            transformed[day] = range(from, parseInt(to) - 1)
          }
        } else {
          transformed[day] = []
        }
      }
      return transformed
    })

    return {
      error,
      days,
      handleFromTimeInput,
      handleToTimeInput,
      fromTimeOptions,
      toTimeOptions,
      schedule,
      transformedSchedule,
      toTimeOptionIsDisabled,
      fromTimeOptionIsDisabled
    }
  }
})
